/**
 * A capa e a marca da mesa: o endereço da imagem que a mesa guarda, ou a arte gerada a partir do id
 * da sala quando não há imagem (ou quando ela não abre). A imagem vinda do Desk Cloud fica guardada
 * como `rpgdesk-file:<id>` e aparece pela rota pública da mesa, com o id do arquivo como versão
 * (docs/spec/desk-cloud.md, "Capa e marca da mesa").
 */
import { serverUrl } from '../serverUrl.js';
import { deskFileRefId, type RoomCard } from '../shared.js';

export type RoomImage = 'cover' | 'logo';

function hash(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Capa gerada: dois tons da mesma família, em diagonal. */
export function generatedCover(seed: string): string {
  const h = hash(seed);
  const hue = h % 360;
  const hue2 = (hue + 30 + ((h >> 9) % 60)) % 360;
  return `linear-gradient(135deg, hsl(${hue} 42% 30%), hsl(${hue2} 46% 16%))`;
}

/**
 * O endereço da imagem guardada (`value`, a capa ou a marca de `roomId`): o arquivo do Desk Cloud vai
 * pela rota pública da mesa, o endereço da web como está; null sem imagem.
 */
export function roomImageSrc(roomId: string, which: RoomImage, value: string | null | undefined): string | null {
  if (!value) return null;
  const fileId = deskFileRefId(value);
  return serverUrl(fileId ? `/api/rooms/${encodeURIComponent(roomId)}/${which}?v=${encodeURIComponent(fileId)}` : value);
}

/** `url("…")` para o CSS, com as aspas, a barra invertida e as quebras de linha escapadas. */
export function cssUrl(src: string): string {
  return `url("${src.replace(/["\\]/g, '\\$&').replace(/[\n\r\f]/g, ' ')}")`;
}

/** A capa da mesa: a imagem dela sobre a gerada, que aparece se a imagem não abrir. */
export function coverOf(card: Pick<RoomCard, 'id' | 'coverUrl'>): string {
  const src = roomImageSrc(card.id, 'cover', card.coverUrl);
  return src ? `${cssUrl(src)}, ${generatedCover(card.id)}` : generatedCover(card.id);
}
